import client from '../client';
import type { ApiResponse } from '../../types/api';
import type { SettingsSchemaField } from '../../types/app';

/**
 * The app-install consent flow (`/apps/authorize`). The backend mounts this
 * under `/api/v1/apps/oauth`, outside the `/dashboard/:storeId` tree, because
 * the merchant has not picked a store yet when the flow starts.
 */

/** Settings schema on the OAuth payload is keyed by field name, not an array. */
export type AuthorizeSettingsSchema = Record<string, Omit<SettingsSchemaField, 'key'>>;

export interface AuthorizeApp {
  id: string;
  name: string;
  slug?: string;
  description?: string;
  logoUrl?: string;
  website?: string;
  privacyPolicyUrl?: string;
  developerEmail?: string;
  settingsSchema?: AuthorizeSettingsSchema;
}

export interface AuthorizeInfo {
  app: AuthorizeApp;
  /** null until a store_id is supplied — the picker phase. */
  store: { id: string; name: string; slug: string } | null;
  requestedScopes: { scope: string; description: string }[];
  state?: string | null;
  isAlreadyInstalled: boolean;
  existingScopes: string[];
  existingSettings: Record<string, unknown>;
}

export interface AuthorizeParams {
  client_id: string;
  redirect_uri: string;
  scope?: string | null;
  state?: string | null;
  store_id?: string | null;
}

export const appAuthorizeApi = {
  info: async (params: AuthorizeParams): Promise<AuthorizeInfo> => {
    const res = await client.get<ApiResponse<AuthorizeInfo>>('/apps/oauth/authorize', {
      params: {
        client_id: params.client_id,
        redirect_uri: params.redirect_uri,
        ...(params.scope ? { scope: params.scope } : {}),
        ...(params.state ? { state: params.state } : {}),
        ...(params.store_id ? { store_id: params.store_id } : {}),
      },
    });
    return res.data.data!;
  },

  /**
   * Grant or deny. Both actions answer with the URL to send the merchant back
   * to — on grant it carries the authorization code, on deny an `access_denied`
   * error — so the caller just navigates to `redirect`.
   */
  submit: async (body: {
    client_id: string;
    redirect_uri: string;
    store_id: string;
    scopes: string[];
    state?: string | null;
    action: 'grant' | 'deny';
    settings?: Record<string, unknown>;
  }): Promise<{ redirect: string }> => {
    const res = await client.post<ApiResponse<{ redirect: string }>>('/apps/oauth/authorize', body);
    return res.data.data!;
  },
};
